<template>
  <BasicModal
    title="common.import"
    okText="g.25100001"
    size="middle"
    destroyOnClose
    :okButtonProps="{
      disabled: hasError,
    }"
    :confirmLoading="importing"
    :closeFunc="async () => !importing && !loading"
    :afterClose="resetData"
    @register="registerModal"
    @ok="doImport"
  >
    <div class="p-16px pt-6px flex items-center">
      <MUpload uploadType="excel" @upload-success="uploadSuccess" />
      <slot name="exportModel">
        <SyncExportBtn
          v-if="templateKey || modelId"
          type="link"
          path="/low-code/lowCode/universal/file/template/download"
          :params="{ templateKey: props.templateKey, modelId: props.modelId }"
        >
          {{ t('c.25090337') }}
        </SyncExportBtn>
      </slot>
      <span class="ml-2 text-[#bdbdbd]">
        {{ t('c.25090336') + ACCEPT_EXTS.split(',').join(' ') }}</span
      >
    </div>
    <div class="px-16px">
      <MTabs
        v-if="isMoreSheet && importData.titleList.length"
        v-model:active-key="tabKey"
        :options="tabsArray"
        @change="onTabChange"
      />
    </div>
    <BasicTable
      v-if="importData.titleList.length || loading"
      class="import-excel-table !px-16px !h-[calc(100%-60px)]"
      @register="registerTable"
    />

    <template #insertFooter>
      <div v-if="importData.infoList.length" class="leading-32px absolute">
        {{ t('c.25090335') }}：{{ importData.infoList.length }} {{ t('c.25090332') }}
        <span v-if="importData.importErrorList.length" class="text-[#f66362]">
          {{ t('c.25090338', { count: importData.importErrorList.length }) }}
        </span>
      </div>
    </template>
  </BasicModal>
</template>

<script setup lang="ts">
  import { Tooltip } from 'ant-design-vue';
  import { Key } from 'ant-design-vue/es/_util/type';
  import { h, nextTick, reactive, ref } from 'vue';
  import { getTableColumns, isArray, message } from '@mts-fe/utils';
  import { MTabs } from '@mts/components/Base';
  import { SyncExportBtn } from '@mts/components/Export';
  import { BasicModal, useModalInner } from '@mts/components/Modal';
  import { BasicColumn, BasicTable, formatCell, useTable } from '@mts/components/Table';
  import { t } from '@mts/hooks/web/useI18n';
  import { createApi } from '@mts/render/api';
  import { MUpload } from '@mts/render/components/Upload';
  import { warn } from '@mts/utils';
  import type { ImportProps } from '..';

  const ACCEPT_EXTS = '.xls,.xlsx';
  const props = withDefaults(defineProps<ImportProps>(), {});

  props.templateKey && warn('templateKey的方式已弃用，请使用modelId');

  const [registerModal, { closeModal }] = useModalInner();
  const resetData = async () => {
    importData.titleList = [];
    importData.infoList = [];
    importData.importErrorList = [];
    hasError.value = true;
  };

  const [registerTable, tableAction] = useTable({
    showTableSetting: false,
    showIndexColumn: false,
    pagination: false,
    bordered: true,
    inset: true,
    rowKey: '行号',
    maxHeight: 780,
  });

  const importing = ref(false);
  const loading = ref(false);
  const importData = reactive({
    titleList: [] as string[],
    infoList: [] as any[],
    importErrorList: [] as { index: number; title: string; msg: string }[],
  });
  const hasError = ref(true);

  const getParams = () => (typeof props.params == 'function' ? props.params() : props.params) || {};
  const formatResData = (res: any) => {
    importData.titleList = res.titleList || [];
    importData.infoList = res.infoList || [];
    importData.importErrorList = res.importErrorList || [];
    const getErrorInfo = (title: string, rowIndex: string) =>
      importData.importErrorList.find((i) => i.title === title && i.index.toString() === rowIndex);
    tableAction.setTableData(importData.infoList);
    tableAction.setColumns(
      getTableColumns(
        importData.titleList.map((title, index) => {
          let obj: BasicColumn = {
            title: title == '行号' ? t('ML_20250224_87') : title,
            dataIndex: title,
            width: index === 0 ? 45 : 100,
            customCell: (record) => ({
              class: getErrorInfo(title, record['行号']) ? 'import-error-cell' : '',
            }),
            customRender: ({ text, record, index }) => {
              const content = formatCell(text, 'split|\n', record, index);

              const curError = getErrorInfo(title, record['行号']);
              return curError
                ? h(Tooltip, { title: curError.msg, color: '#f57473' }, () => content || '-')
                : content;
            },
          };
          return obj;
        }),
      ),
    );
  };
  const tabsArray = ref<any[]>([]);
  const tabKey = ref('');
  const isMoreSheet = ref(false);
  const uploadFileUrl = ref('');
  const onTabChange = (key: Key) => {
    tabKey.value = key as string;
    formatResData(tabsArray.value.find((item) => item.value === key));
  };
  function uploadSuccess({ fileUrl }) {
    resetData();
    uploadFileUrl.value = fileUrl;
    loading.value = true;
    nextTick(() => tableAction.setLoading(true));
    props.action &&
      createApi(props.action)({
        ...getParams(),
        templateKey: props.templateKey,
        fileUrl,
        realUpload: false,
      })
        .then((res) => {
          hasError.value = false;
          if (isArray(res)) {
            isMoreSheet.value = true;
            tabsArray.value = res.map((item) => {
              return {
                ...item,
                label: item.templateKey,
                value: item.templateKey,
              };
            });
            hasError.value = tabsArray.value.some((item) => !!item.importErrorList?.length);
            tabKey.value = res[0]?.templateKey;
            formatResData(tabsArray.value[0]);
          } else {
            isMoreSheet.value = false;
            hasError.value = res.infoList?.length ? !!res.importErrorList?.length : true;
            formatResData(res);
          }
        })
        .finally(() => ((loading.value = false), tableAction.setLoading(false)));
  }

  const emit = defineEmits(['ok', 'register']);
  async function doImport() {
    if (importing.value) return;

    importing.value = true;

    if (isMoreSheet.value === true) {
      props.action &&
        createApi(props.action)({
          ...getParams(),
          templateKey: props.templateKey,
          fileUrl: uploadFileUrl.value,
          realUpload: true,
        })
          .then((res) => {
            message.success(t('c.25090229'));
            closeModal();
            emit('ok', res);
          })
          .finally(() => {
            importing.value = false;
          });
    } else {
      props.action &&
        createApi(props.action)({
          ...getParams(),
          templateKey: props.templateKey,
          infoList: importData.infoList,
          titleList: importData.titleList,
          realUpload: true,
        })
          .then((res) => {
            message.success(t('c.25090229'));
            closeModal();
            emit('ok', res);
          })
          .finally(() => {
            importing.value = false;
          });
    }
  }
</script>

<style lang="less">
  .import-excel-table .ant-table-wrapper .ant-table .ant-table-tbody > tr > td.import-error-cell {
    background-color: #fcdbd9;
    color: #f66362;

    span {
      display: block;
      cursor: pointer;
    }
  }
</style>
